import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { HYPOTHESIS_STATUSES, HYPOTHESIS_VERDICTS, RARITY_DIMENSIONS, RARITY_DIMENSION_LABELS, RELATION_KIND_LABELS, VERDICT_LABELS, hasRoleAtLeast, type EntityType, type HypothesisStatus, type RarityDimension, type RelationKind, type Verdict } from '@raro/domain';
import { Breadcrumbs, Button, Drawer, Field, Figure, Label, Notice, Select, Serif, Tag, Textarea } from '@raro/ui';
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router';
import { ChallengePanel } from '../components/ChallengePanel';
import { EntityPicker } from '../components/EntityPicker';
import { ImagePicker } from '../components/ImagePicker';
import { RelationList } from '../components/RelationList';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { HYPOTHESIS_STATUS_LABELS, formatDate } from '../lib/format';
import type { EntitySummary, HypothesisDetail, Initiative, Judgment, Me, ProjectDetail } from '../lib/types';

export function HipotesePage() {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const { me } = useAuth();
  const hyp = useQuery({ queryKey: ['hypothesis', id], queryFn: () => api.get<HypothesisDetail>(`/hypotheses/${id}`) });
  const project = useQuery({ queryKey: ['project', hyp.data?.projectId], queryFn: () => api.get<ProjectDetail>(`/projects/${hyp.data!.projectId}`), enabled: Boolean(hyp.data?.projectId) });
  const [scores, setScores] = useState<Partial<Record<RarityDimension, number>>>({});
  const [linking, setLinking] = useState<{ kind: RelationKind; entity?: EntitySummary; note: string } | null>(null);
  const judgments = useQuery({ queryKey: ['judgments', 'hypothesis', id], queryFn: () => api.get<Judgment[]>(`/judgments/hypothesis/${id}`) });
  const initiatives = useQuery({ queryKey: ['initiatives', hyp.data?.projectId], queryFn: () => api.get<Initiative[]>(`/projects/${hyp.data!.projectId}/initiatives`), enabled: Boolean(hyp.data?.projectId) });
  const [note, setNote] = useState('');
  const [picking, setPicking] = useState(false);

  useEffect(() => {
    if (hyp.data) setScores(hyp.data.scores);
  }, [hyp.data]);

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['hypothesis', id] });
    void qc.invalidateQueries({ queryKey: ['hypotheses'] });
    void qc.invalidateQueries({ queryKey: ['contradictions'] });
    void qc.invalidateQueries({ queryKey: ['summary'] });
    void qc.invalidateQueries({ queryKey: ['judgments', 'hypothesis', id] });
    void qc.invalidateQueries({ queryKey: ['ver'] });
  };
  const judge = useMutation({ mutationFn: (verdict: Verdict) => api.post(`/projects/${hyp.data!.projectId}/judgments`, { entityType: 'hypothesis', entityId: id, verdict, note: note || undefined }), onSuccess: () => { setNote(''); invalidate(); } });
  const update = useMutation({ mutationFn: (body: Record<string, unknown>) => api.patch(`/hypotheses/${id}`, body), onSuccess: invalidate });
  const relate = useMutation({
    mutationFn: (l: NonNullable<typeof linking>) => api.post(`/hypotheses/${id}/relations`, { kind: l.kind, entityType: l.entity!.type, entityId: l.entity!.id, note: l.note || undefined }),
    onSuccess: () => {
      setLinking(null);
      invalidate();
    },
  });
  const pin = useMutation({ mutationFn: () => api.post(`/projects/${hyp.data!.projectId}/wall`, { kind: 'ref', refType: 'hypothesis', refId: id }), onSuccess: () => qc.invalidateQueries({ queryKey: ['wall'] }) });

  if (hyp.isPending) return <div className="rr-page rr-faint">carregando…</div>;
  if (hyp.isError || !hyp.data) return <div className="rr-page"><Notice tone="error">não encontramos esta hipótese.</Notice></div>;
  const h = hyp.data;
  const role = roleFor(me, project.data?.organizationId);
  const canEdit = hasRoleAtLeast(role, 'analyst');
  const canJudge = hasRoleAtLeast(role, 'partner');
  const supports = h.relations.filter((r) => r.kind === 'supports' && r.toId === id);
  const contradicts = h.relations.filter((r) => r.kind === 'contradicts' && r.toId === id);
  const others = h.relations.filter((r) => !supports.includes(r) && !contradicts.includes(r));
  const mine = judgments.data?.find((j) => j.userId === me?.id) ?? null;
  const related = initiatives.data?.filter((i) => i.hypothesisId === id) ?? [];

  return (
    <>
      <div className="rr-topbar">
        <Breadcrumbs items={[
          { label: 'Projetos', render: (l) => <Link to="/projetos">{l}</Link> },
          ...(project.data ? [{ label: project.data.organization.name }, { label: project.data.name, render: (l: string) => <Link to={`/projetos/${project.data!.id}`}>{l}</Link> }] : []),
          { label: `Hipótese ${String(h.number).padStart(2, '0')}` },
        ]} />
        <div className="rr-row">
          {canEdit ? (
            <Select value={h.status} onChange={(e) => update.mutate({ status: e.target.value as HypothesisStatus })} style={{ height: 30, width: 'auto', fontSize: 'var(--rr-text-xs)' }}>
              {HYPOTHESIS_STATUSES.map((s) => <option key={s} value={s}>{HYPOTHESIS_STATUS_LABELS[s]}</option>)}
            </Select>
          ) : <Tag tone="outline">{HYPOTHESIS_STATUS_LABELS[h.status]}</Tag>}
          {canEdit && <Button size="sm" onClick={() => pin.mutate()} disabled={pin.isPending}>colocar na parede</Button>}
        </div>
      </div>

      <div className="rr-page rr-page--tight rr-stack rr-stack--7">
        <header className="rr-stack rr-stack--3">
          <Label tone="accent">Hipótese {String(h.number).padStart(2, '0')}</Label>
          <h1 className="rr-display">{h.title}</h1>
          <p className="rr-heading rr-muted"><Serif italic>{h.statement}</Serif></p>
          {h.rationale && <p className="rr-muted" style={{ maxWidth: 720 }}>{h.rationale}</p>}
          <div className="rr-row" style={{ fontSize: 'var(--rr-text-xs)', color: 'var(--rr-text-3)' }}>
            <span>{h.generatedBy === 'ai' ? 'proposta pela Machine' : 'proposta por pessoa'}</span>
            <span>{formatDate(h.createdAt)}</span>
            <span style={{ color: 'var(--rr-up)' }}>{supports.length} sustentam</span>
            <span style={{ color: 'var(--rr-down)' }}>{contradicts.length} contradizem</span>
          </div>
        </header>

        <ChallengePanel hypothesisId={id} projectId={h.projectId} canEdit={canEdit} />

        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.3fr) minmax(300px, 1fr)', gap: 'var(--rr-space-7)', alignItems: 'start' }}>
          <div className="rr-stack rr-stack--6">
            <section className="rr-section">
              <div className="rr-section__head">
                <Label>Provas a favor</Label>
                {canEdit && <Button size="sm" variant="quiet" onClick={() => setLinking({ kind: 'supports', note: '' })}>+ sustenta</Button>}
              </div>
              <RelationList relations={supports} self={{ type: 'hypothesis', id }} canEdit={canEdit} />
            </section>
            <section className="rr-section">
              <div className="rr-section__head">
                <Label>Provas contra</Label>
                {canEdit && <Button size="sm" variant="quiet" onClick={() => setLinking({ kind: 'contradicts', note: '' })}>+ contradiz</Button>}
              </div>
              <RelationList relations={contradicts} self={{ type: 'hypothesis', id }} canEdit={canEdit} />
            </section>
            <section className="rr-section">
              <div className="rr-section__head">
                <Label>Outras relações</Label>
                {canEdit && <Button size="sm" variant="quiet" onClick={() => setLinking({ kind: 'answers', note: '' })}>+ relacionar</Button>}
              </div>
              <RelationList relations={others} self={{ type: 'hypothesis', id }} canEdit={canEdit} />
            </section>
          </div>

          <aside className="rr-card rr-stack rr-stack--5" style={{ position: 'sticky', top: 'var(--rr-space-5)' }}>
            <Figure src={h.image?.url} ratio="16 / 10" onClick={canEdit ? () => setPicking(true) : undefined}>
              <span className="rr-label" style={{ color: 'var(--rr-text-2)' }}>{h.image ? h.image.name : canEdit ? 'escolher uma foto' : ''}</span>
            </Figure>
            <div className="rr-stack rr-stack--3">
              <div className="rr-stack rr-stack--1">
                <Label tone="accent">Bater o martelo</Label>
                <p className="rr-faint" style={{ fontSize: 'var(--rr-text-xs)' }}>{canJudge ? 'a Machine propõe; você decide o que fazer com esta hipótese.' : 'só um sócio decide o que fazer com a hipótese.'}</p>
              </div>
              {canJudge && (
                <>
                  <div className="rr-verdicts">
                    {HYPOTHESIS_VERDICTS.map((v) => (
                      <button key={v} type="button" className={`rr-verdict rr-verdict--${v}${mine?.verdict === v ? ' rr-verdict--on' : ''}`} onClick={() => judge.mutate(v)} disabled={judge.isPending}>{VERDICT_LABELS[v]}</button>
                    ))}
                  </div>
                  <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="por quê? (fica no dataset da Raro)" />
                  {judge.isError && <Notice tone="error">{(judge.error as Error).message}</Notice>}
                </>
              )}
              {judgments.data && judgments.data.length > 0 && (
                <div className="rr-stack rr-stack--1">
                  {judgments.data.map((j) => <span key={j.id} className="rr-faint" style={{ fontSize: 'var(--rr-text-xs)' }}><b style={{ color: 'var(--rr-text-2)' }}>{j.user?.name ?? '—'}</b> · {VERDICT_LABELS[j.verdict]} · {formatDate(j.createdAt)}{j.note ? ` · “${j.note}”` : ''}</span>)}
                </div>
              )}
              {related.length > 0 && (
                <div className="rr-stack rr-stack--1">
                  <Label>Iniciativas</Label>
                  {related.map((i) => <Link key={i.id} to={`/projetos/${h.projectId}?s=iniciativas`} className="rr-faint" style={{ fontSize: 'var(--rr-text-xs)', textDecoration: 'underline', textUnderlineOffset: 3 }}>{i.kind === 'experiment' ? 'experimento' : 'iniciativa'}: {i.title} · {i.status}</Link>)}
                </div>
              )}
            </div>
            <div className="rr-stack rr-stack--1">
              <Label>Julgamento Raro</Label>
              <p className="rr-faint" style={{ fontSize: 'var(--rr-text-xs)' }}>dimensões do O RARO. nunca verdade absoluta. {canJudge ? 'arraste para julgar.' : 'só sócios julgam.'}</p>
            </div>
            <div className="rr-stack rr-stack--3">
              {RARITY_DIMENSIONS.map((d) => (
                <div key={d} className="rr-score">
                  <span className="rr-score__label">{RARITY_DIMENSION_LABELS[d]}</span>
                  <input type="range" min={0} max={100} value={scores[d] ?? 0} disabled={!canJudge} onChange={(e) => setScores({ ...scores, [d]: Number(e.target.value) })} onPointerUp={() => scores[d] !== h.scores[d] && update.mutate({ scores: { [d]: scores[d] ?? 0 } })} onKeyUp={() => scores[d] !== h.scores[d] && update.mutate({ scores: { [d]: scores[d] ?? 0 } })} />
                  <span className="rr-score__value">{scores[d] ?? '—'}</span>
                </div>
              ))}
            </div>
            <p className="rr-faint" style={{ fontSize: 'var(--rr-text-xs)' }}>Machine vê assim. Raro julga.</p>
          </aside>
        </div>
      </div>

      {project.data && <ImagePicker open={picking} onClose={() => setPicking(false)} organizationId={project.data.organizationId} projectId={h.projectId} current={h.imageFileId ?? null} onPick={(fid) => update.mutate({ imageFileId: fid })} eyebrow="Foto da hipótese" title={h.title} />}
      <Drawer open={Boolean(linking)} onClose={() => setLinking(null)} eyebrow="Relação" title={linking ? `o que ${RELATION_KIND_LABELS[linking.kind]} esta hipótese?` : ''}>
        {linking && (
          <div className="rr-stack rr-stack--4">
            <Field label="Tipo de relação" htmlFor="rel-kind">
              <Select id="rel-kind" value={linking.kind} onChange={(e) => setLinking({ ...linking, kind: e.target.value as RelationKind })}>
                {(['supports', 'contradicts', 'answers', 'raises', 'related_to', 'tests'] as RelationKind[]).map((k) => <option key={k} value={k}>{RELATION_KIND_LABELS[k]}</option>)}
              </Select>
            </Field>
            <Field label="Objeto" htmlFor="rel-ent" hint={linking.entity ? `selecionado: ${linking.entity.title}` : 'evidência, observação, sinal, pergunta…'}>
              <EntityPicker projectId={h.projectId} types={['evidence', 'observation', 'signal', 'question', 'source'] as EntityType[]} exclude={[{ type: 'hypothesis', id }]} selected={linking.entity ? { type: linking.entity.type, id: linking.entity.id } : null} onSelect={(e) => setLinking({ ...linking, entity: e })} />
            </Field>
            <Field label="Nota" htmlFor="rel-note" hint="opcional. por que essa relação existe?">
              <Textarea id="rel-note" rows={2} value={linking.note} onChange={(e) => setLinking({ ...linking, note: e.target.value })} />
            </Field>
            {relate.isError && <Notice tone="error">{(relate.error as Error).message}</Notice>}
            <div className="rr-row">
              <Button variant="primary" disabled={!linking.entity || relate.isPending} onClick={() => relate.mutate(linking)}>criar relação</Button>
              <Button variant="quiet" onClick={() => setLinking(null)}>cancelar</Button>
            </div>
          </div>
        )}
      </Drawer>
    </>
  );
}

function roleFor(me: Me | null, organizationId: string | undefined) {
  if (!me || !organizationId) return 'client' as const;
  const direct = me.memberships.find((m) => m.organizationId === organizationId)?.role;
  if (direct && me.internalRole) return hasRoleAtLeast(direct, me.internalRole) ? direct : me.internalRole;
  return direct ?? me.internalRole ?? ('client' as const);
}
